/**
 * Helpers for push-down stacks.
 *
 * Templates are absolutely positioned, so a paragraph that wraps to more lines
 * than designed overlaps whatever sits below it. A stack fixes that for a set
 * of vars the designer marks: the template is laid out for its minimal case,
 * and at render time the stitcher measures the wrapped text and pushes the
 * members below it down by the extra height. Nothing is ever pulled up, and
 * only vars in a stack move.
 *
 * A var joins a stack through its metadata:
 *
 *   "stack": {
 *     "id": "body",
 *     "mode": "preserve_gap",
 *     "margin": 0,
 *     "bottom_margin": 0,
 *     "shrink_to_fit": false
 *   }
 *
 *   id            - required - members with the same id on the same page form
 *                              one stack
 *   mode          - "preserve_gap" pushes as soon as a text outgrows its
 *                   designed box; "contact" only once it comes within `margin`
 *                   of the member below
 *   margin        - contact mode only, px kept clear between neighbours
 *   bottom_margin - px kept clear at the bottom edge of the card
 *   shrink_to_fit - shrink the text that grew rather than fail the render when
 *                   the pushed stack no longer fits on the card
 *
 * and keys that belong to the member they are on:
 *
 *   shrink_with_stack - whether shrink_to_fit may shrink this member's text.
 *                   Absent means yes. Not the per-element fixed box + shrink
 *   min_font_size - px floor for shrinking this member's text, never below
 *                   the system floor
 *   boundary      - a line the member's pushed bottom must not pass, in place
 *                   of the card bottom: its y in the design as the editor
 *                   shows it (px from the top of the design). It has no
 *                   identity of its own: members with the same value share a
 *                   line on the canvas, and moving the line rewrites them all
 *
 * All px are template pixels (300 dpi), the space pos_* and size_* are in.
 *
 * The server accepts the settings on any member and merges them, failing the
 * render if two members disagree. The editor sidesteps that by writing the
 * same full settings onto every member of a stack whenever anything about it
 * changes, so no member is special and deleting one never loses the settings.
 *
 * Whether a text shrinks is chosen per text (in the text box settings, next
 * to shrinking to its own box), so the editor never asks for shrink_to_fit:
 * it is on exactly when some text member shrinks with the stack, and then
 * every member says explicitly whether it does.
 *
 * The validation here mirrors the server's rules and messages so problems
 * surface in the editor rather than on save.
 */

export const STACK_MODES = ['preserve_gap', 'contact'];

/** 6 pt at 300 dpi. The server rejects anything lower. */
export const STACK_MIN_FONT_SIZE = 25;

export const STACK_DEFAULTS = Object.freeze({
  mode: 'preserve_gap',
  margin: 0,
  bottom_margin: 0,
  shrink_to_fit: false,
});

export const STACK_SETTING_KEYS = Object.keys(STACK_DEFAULTS);

/** Keys that belong to one member rather than to the stack. */
export const STACK_MEMBER_KEYS = [
  'shrink_with_stack',
  'min_font_size',
  'boundary',
];

const KNOWN_KEYS = ['id', ...STACK_MEMBER_KEYS, ...STACK_SETTING_KEYS];

const isPlainObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isNumber = value => typeof value === 'number' && Number.isFinite(value);

/**
 * The stack metadata on a fabric object or exported var, or null when it has
 * none. Only the presence of the key is checked; use validateStack for its
 * shape.
 */
export const stackOf = object => {
  const stack = object?.metadata?.stack;
  return stack === undefined || stack === null ? null : stack;
};

/** The stack id of an object, or null when it is not in a (well-formed) stack. */
export const stackIdOf = object => {
  const stack = stackOf(object);
  return isPlainObject(stack) && typeof stack.id === 'string' ? stack.id : null;
};

/**
 * Checks one var's stack metadata the way the server does. Returns an object
 * of messages keyed by the offending setting (`id`, `mode`, ..., or `stack`
 * for a problem with the value as a whole); empty when valid.
 */
export const validateStack = stack => {
  if (!isPlainObject(stack)) return { stack: 'stack must be a map' };

  const errors = {};

  if (typeof stack.id !== 'string') {
    errors.id = 'stack id is required and must be a string';
  } else if (stack.id.trim() === '') {
    errors.id = 'stack id must not be blank';
  }

  const unknown = Object.keys(stack)
    .filter(key => !KNOWN_KEYS.includes(key))
    .sort();
  if (unknown.length) {
    errors.stack = `unknown stack settings: ${unknown.join(', ')}`;
  }

  if ('mode' in stack && !STACK_MODES.includes(stack.mode)) {
    errors.mode = `stack mode must be one of: ${STACK_MODES.join(', ')}`;
  }

  for (const key of ['margin', 'bottom_margin']) {
    if (key in stack && !(isNumber(stack[key]) && stack[key] >= 0)) {
      errors[key] = `stack ${key} must be a non-negative number`;
    }
  }

  if ('shrink_to_fit' in stack && typeof stack.shrink_to_fit !== 'boolean') {
    errors.shrink_to_fit = 'stack shrink_to_fit must be a boolean';
  }

  if (
    'shrink_with_stack' in stack &&
    typeof stack.shrink_with_stack !== 'boolean'
  ) {
    errors.shrink_with_stack = 'stack shrink_with_stack must be a boolean';
  }

  if (
    'boundary' in stack &&
    !(isNumber(stack.boundary) && stack.boundary >= 0)
  ) {
    errors.boundary = 'stack boundary must be a non-negative number';
  }

  if (
    'min_font_size' in stack &&
    !(
      isNumber(stack.min_font_size) &&
      stack.min_font_size >= STACK_MIN_FONT_SIZE
    )
  ) {
    errors.min_font_size = `stack min_font_size must be a number of at least ${STACK_MIN_FONT_SIZE}`;
  }

  return errors;
};

export const isStackValid = stack =>
  Object.keys(validateStack(stack)).length === 0;

/**
 * The settings of a stack with the defaults filled in. Anything that is not a
 * known setting is dropped, so a stack read back from older or hand-edited
 * metadata comes out with exactly the keys the server knows.
 */
export const stackSettings = stack => {
  const settings = { ...STACK_DEFAULTS };
  if (!isPlainObject(stack)) return settings;
  for (const key of STACK_SETTING_KEYS) {
    if (stack[key] !== undefined) settings[key] = stack[key];
  }
  return settings;
};

/**
 * The full stack value to write onto every member: the id plus every setting,
 * defaults filled in.
 */
export const buildStack = (id, settings = {}) => ({
  id,
  ...stackSettings(settings),
});

/** Whether shrink_to_fit may shrink this member's text. Absent means yes. */
export const memberShrinks = object =>
  stackOf(object)?.shrink_with_stack !== false;

/** Whether the member is shrunk when its stack runs out of room. */
export const shrinksWithStack = object =>
  stackOf(object)?.shrink_to_fit === true && memberShrinks(object);

/** The member's floor for shrinking with the stack. */
export const memberMinFontSize = object => {
  const size = stackOf(object)?.min_font_size;
  return isNumber(size) ? size : STACK_MIN_FONT_SIZE;
};

/** The member's boundary, or null when it has none. */
export const memberBoundary = object => {
  const boundary = stackOf(object)?.boundary;
  return isNumber(boundary) ? boundary : null;
};

/**
 * Checks a boundary for a member whose designed bottom is at
 * `designedBottom` (design px): a line above it could never be kept.
 */
export const boundaryError = (boundary, designedBottom) => {
  const shape = validateStack({ id: 'x', boundary }).boundary;
  if (shape) return shape;
  return boundary < designedBottom
    ? "stack boundary must be below the member's designed bottom"
    : null;
};

/**
 * The stack value for one member: the shared stack plus its boundary when it
 * has one, and, when the stack shrinks, the member's own choice, with its
 * floor when it shrinks too. A member of a stack that never shrinks and has
 * no boundary carries nothing extra.
 */
export const memberStack = (
  stack,
  { shrinks = false, minFontSize, boundary } = {}
) => {
  const own = isNumber(boundary) ? { ...stack, boundary } : stack;
  if (!stack.shrink_to_fit) return own;
  if (!shrinks) return { ...own, shrink_with_stack: false };
  return {
    ...own,
    shrink_with_stack: true,
    ...(minFontSize !== undefined &&
      minFontSize !== null && { min_font_size: minFontSize }),
  };
};

/**
 * The stack values to write onto a stack's members, as a Map. The settings
 * are shared; shrink_to_fit is derived, on exactly when some text member
 * shrinks with the stack. `choiceOf(member)` gives its own keys,
 * { shrinks, minFontSize, boundary }; only text (`isText`) can shrink.
 */
export const memberStacks = (id, members, settings, choiceOf, isText) => {
  const choices = new Map(
    members.map(member => {
      const choice = choiceOf(member);
      return [member, { ...choice, shrinks: isText(member) && choice.shrinks }];
    })
  );
  const stack = buildStack(id, {
    ...settings,
    shrink_to_fit: [...choices.values()].some(choice => choice.shrinks),
  });
  return new Map(
    members.map(member => [member, memberStack(stack, choices.get(member))])
  );
};

/** A copy of the metadata with the stack set. Never mutates. */
export const withStack = (metadata, stack) => ({ ...(metadata || {}), stack });

/** A copy of the metadata with the stack removed. Never mutates. */
export const withoutStack = metadata => {
  const { stack, ...rest } = metadata || {};
  return rest;
};

/**
 * The settings shared by a set of members: the first member's. The editor
 * keeps every member's settings identical, so this is only a tie-break for
 * stacks that were set by hand with settings on one member alone, in which
 * case the member carrying any explicit setting wins over one with just an id.
 */
export const mergedStackSettings = members => {
  const stacks = members.map(stackOf).filter(isPlainObject);
  const explicit = stacks.find(stack =>
    STACK_SETTING_KEYS.some(key => stack[key] !== undefined)
  );
  return stackSettings(explicit || stacks[0]);
};

/**
 * Members in flow order: top to bottom as designed, with canvas order (the
 * order given) breaking ties. In the editor design "down" is screen down in
 * both orientations, so no axis logic is needed here.
 */
export const flowOrder = (objects, topOf = object => object.top) =>
  objects
    .map((object, index) => ({ object, index, top: topOf(object) }))
    .sort((a, b) => a.top - b.top || a.index - b.index)
    .map(entry => entry.object);

/**
 * Groups objects by stack id. Objects outside any stack are left out. The
 * caller limits the objects to one page; the same id on two pages is two
 * independent stacks.
 */
export const groupByStack = objects => {
  const groups = new Map();
  for (const object of objects) {
    const id = stackIdOf(object);
    if (id === null) continue;
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push(object);
  }
  return groups;
};

/**
 * The boundary lines of a set of members: one per distinct value, with the
 * members that carry it, top to bottom.
 */
export const boundaryLines = members => {
  const lines = new Map();
  for (const member of members) {
    const y = memberBoundary(member);
    if (y === null) continue;
    if (!lines.has(y)) lines.set(y, []);
    lines.get(y).push(member);
  }
  return [...lines.entries()]
    .sort(([a], [b]) => a - b)
    .map(([y, carriers]) => ({ y, members: carriers }));
};

/** The first `stack-N` not already in use. */
export const nextStackId = (existingIds, prefix = 'stack') => {
  const taken = new Set(existingIds);
  let n = 1;
  while (taken.has(`${prefix}-${n}`)) n += 1;
  return `${prefix}-${n}`;
};

/**
 * Turns the strings a settings form yields into a settings object, reporting
 * what cannot be parsed. Blank numeric fields fall back to the defaults so a
 * cleared field does not become NaN. Keys the form does not mention are left
 * at their defaults (shrink_to_fit is derived from the members, not set).
 */
export const parseStackSettingsForm = form => {
  const settings = { ...STACK_DEFAULTS };
  const errors = {};

  if (form.mode !== undefined) settings.mode = form.mode;

  for (const key of ['margin', 'bottom_margin']) {
    const raw = form[key];
    if (raw === undefined || raw === null || String(raw).trim() === '')
      continue;
    const value = Number(raw);
    if (Number.isFinite(value)) {
      settings[key] = value;
    } else {
      errors[key] = `stack ${key} must be a number`;
    }
  }

  return {
    settings,
    errors: { ...validateStack({ id: 'x', ...settings }), ...errors },
  };
};

/**
 * A short, human label for a member in a list: what the designer would
 * recognise it by.
 */
export const describeStackMember = object => {
  const text = object?.text ?? object?.originalText;
  if (typeof text === 'string' && text.trim() !== '') {
    const line = text.trim().split('\n')[0];
    return line.length > 32 ? `${line.slice(0, 31)}…` : line;
  }
  const label = object?.label;
  if (typeof label === 'string' && label.trim() !== '') return label;
  return object?.subtype || object?.type || 'object';
};

/**
 * The metadata messages in a failed template save. The server answers bad
 * var metadata with 422 and nested changeset errors:
 *
 *   { errors: { ad_template_vars: [ {}, { metadata: ["stack id must not be blank"] } ] } }
 *
 * or, for a double-sided template, under ad_template_pages[i].ad_template_vars.
 * Returns the messages in order, empty when the failure was something else.
 */
export const metadataSaveErrors = error => {
  const errors = error?.data?.errors;
  if (!isPlainObject(errors)) return [];

  const fromVars = vars =>
    (Array.isArray(vars) ? vars : []).flatMap(varErrors =>
      isPlainObject(varErrors) && Array.isArray(varErrors.metadata)
        ? varErrors.metadata.filter(m => typeof m === 'string')
        : []
    );

  const messages = [
    ...fromVars(errors.ad_template_vars),
    ...(Array.isArray(errors.ad_template_pages)
      ? errors.ad_template_pages.flatMap(page =>
          isPlainObject(page) ? fromVars(page.ad_template_vars) : []
        )
      : []),
  ];

  return messages;
};

/** The stack-related messages in a failed template save. */
export const stackSaveErrors = error =>
  metadataSaveErrors(error).filter(message => /\bstack\b/i.test(message));

/**
 * The stitcher's reason for a failed preview, when it gave one. A stack that
 * does not fit fails the preview with
 *
 *   { error: { message: "Error assembling image.",
 *              data: { reason: "Template stack does not fit: ...", data: {...} } } }
 *
 * Returns null for any other failure so the caller keeps its generic message.
 */
export const previewFailureReason = error => {
  const reason = error?.data?.error?.data?.reason;
  return typeof reason === 'string' && reason.trim() !== '' ? reason : null;
};
